import { toast } from "sonner";

export interface BulkActionConfig<T = string | number> {
  selectedIds: T[];
  mutationFn?: (id: T) => Promise<any>;
  bulkMutationFn?: (ids: T[]) => Promise<any>; // New: Single API call for all IDs
  // Legacy properties supported for backward compatibility
  actionFn?: (id: T) => Promise<any>;
  actionName?: string;
  itemType?: string;
  requireConfirm?: boolean;
  confirmMessage?: string;
  confirmFn?: (message: string) => Promise<boolean>;
  
  onSuccess?: () => void;
  messages?: {
    loading?: string;
    success?: string;
    partial?: string;
    error?: string;
  };
}

/**
 * Executes a bulk action on a list of IDs.
 * Uses bulkMutationFn if provided (1 API call), otherwise falls back to Promise.allSettled (N API calls).
 */
export const executeBulkAction = async <T = string | number>({
  selectedIds,
  mutationFn,
  bulkMutationFn,
  actionFn,
  actionName,
  itemType,
  requireConfirm,
  confirmMessage,
  confirmFn,
  onSuccess,
  messages,
}: BulkActionConfig<T>): Promise<boolean> => {
  if (!selectedIds.length) return false;
  
  const fn = mutationFn || actionFn;
  if (!fn && !bulkMutationFn) {
      console.error("executeBulkAction: Missing mutationFn, actionFn, or bulkMutationFn");
      return false;
  }

  if (requireConfirm) {
      const msg = confirmMessage || `Bạn có chắc muốn ${actionName || 'thực hiện'} ${selectedIds.length} ${itemType || 'mục'} đã chọn?`;
      if (confirmFn) {
          const confirmed = await confirmFn(msg);
          if (!confirmed) return false;
      } else {
          if (!window.confirm(msg)) {
              return false;
          }
      }
  }

  const loadingMsg = messages?.loading || "Đang xử lý...";
  const toastId = toast.loading(loadingMsg);

  try {
    if (bulkMutationFn) {
      // Execute as a single atomic batch request
      const res = await bulkMutationFn(selectedIds);
      
      const successCount = res?.successCount ?? selectedIds.length;
      const skippedCount = res?.skippedCount ?? 0;
      
      if (skippedCount === 0) {
        toast.success(messages?.success || `Đã ${actionName || 'thực hiện'} ${successCount} ${itemType || 'mục'} thành công.`, { id: toastId });
        onSuccess?.();
        return true;
      }
      
      if (successCount > 0) {
        toast.error(
          messages?.partial || `Thành công ${successCount}/${selectedIds.length}. Lỗi ${skippedCount} mục.`, 
          { id: toastId, duration: 5000 }
        );
      } else {
        toast.error(
          messages?.error || `Thất bại toàn bộ ${selectedIds.length} mục.`, 
          { id: toastId, duration: 5000 }
        );
      }
      onSuccess?.();
      return false;
    }

    // Fallback to Promise.allSettled (N+1 Calls)
    const results = await Promise.allSettled(
      selectedIds.map((id) => fn!(id))
    );

    const succeeded = results.filter((r) => r.status === "fulfilled").length;
    const failed = results.filter((r) => r.status === "rejected");

    if (failed.length === 0) {
      toast.success(messages?.success || `Đã ${actionName || 'thực hiện'} ${selectedIds.length} ${itemType || 'mục'} thành công.`, { id: toastId });
      onSuccess?.();
      return true;
    }

    if (succeeded > 0) {
      const errorDetail = failed[0].status === "rejected" ? failed[0].reason?.message || "Lỗi không xác định" : "";
      toast.error(
        messages?.partial || `Thành công ${succeeded}/${selectedIds.length}. Lỗi 1: ${errorDetail}`, 
        {
          id: toastId,
          duration: 5000,
        }
      );
    } else {
      const errorDetail = failed[0].status === "rejected" ? failed[0].reason?.message || "Lỗi không xác định" : "";
      toast.error(
        messages?.error || `Thất bại toàn bộ. Lỗi 1: ${errorDetail}`, 
        {
          id: toastId,
          duration: 5000,
        }
      );
    }
    
    onSuccess?.();
    return false;
  } catch (error: any) {
    toast.error(error.message || "Đã xảy ra lỗi trong quá trình xử lý", {
      id: toastId,
    });
    return false;
  }
};
